// 1. Sizes every dropdown to one height and fits square feature images to it (based on the
//    tallest dropdown's links), frames them with an even gap (--feature-inset) at the top,
//    bottom and right-hand edge, and works out how many squares fit beside each dropdown's
//    links (data-squares).
// 2. Indents the dropdown links (--mujo-menu-indent) so the first column lines up with where
//    the logo's lettering starts (logo images often have empty space on their left).
// 3. Shows a preview of the hovered collection link over the dropdown's first square.
// 4. Mobile: adds the logo and a search button to the top bar of the slide-out menu (sized to
//    match the page header), and moves the prepared squares (<template data-curated-for>)
//    into each section of the menu. This is done while the menu is closed (on load, and
//    again whenever the theme re-renders the header), never while a customer is touching
//    the open menu, so nothing shifts under their finger.

const LINK_SELECTOR = '.mega-menu__layout--curated .mega-menu__grid a.mega-menu__link[href]';
const IMAGE_SIZES = '440px';
const MIN_FEATURE = 280;
const MAX_FEATURE = 440;
const MIN_LINK_COLUMN = 150;
const controllers = new WeakMap();
const logoInkCache = new Map();
let measuredWidth = 0;
let squareSize = 0;
let bleedSize = 0;
let menuIndent = 0;

const normalise = (href) => {
  try {
    return new URL(href, window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return href;
  }
};

const currentPath = normalise(window.location.href);

/* ---------- Mobile drawer ---------- */

/** Match the menu's top bar to the page header's height and logo size. */
const syncDrawerBar = (drawer) => {
  const row = document.querySelector('header .header__row--top') || document.querySelector('header .header__row');
  const logo = document.querySelector('header .header-logo__image');
  const rowHeight = row?.getBoundingClientRect().height;
  const logoHeight = logo?.getBoundingClientRect().height;
  if (rowHeight) drawer.style.setProperty('--mujo-drawer-bar-height', `${Math.round(rowHeight)}px`);
  if (logoHeight) drawer.style.setProperty('--mujo-drawer-logo-height', `${Math.round(logoHeight)}px`);
};

/** Close button | logo | search, across the top of the slide-out menu. */
const mountDrawerBar = () => {
  const drawer = document.querySelector('.menu-drawer');
  const close = drawer?.querySelector(':scope > .menu-drawer__close-button');
  if (!drawer) return;
  if (drawer.querySelector(':scope > .mujo-drawer-bar') || !close) {
    if (!drawer.style.getPropertyValue('--mujo-drawer-bar-height')) syncDrawerBar(drawer);
    return;
  }

  const bar = document.createElement('div');
  bar.className = 'mujo-drawer-bar';

  const start = document.createElement('div');
  start.className = 'mujo-drawer-bar__start';
  bar.append(start);

  const logoImg = document.querySelector('header .header-logo__image');
  if (logoImg) {
    const link = document.createElement('a');
    link.className = 'mujo-drawer-bar__logo';
    link.href = window.Shopify?.routes?.root || '/';
    link.setAttribute('aria-label', 'Home');
    const img = document.createElement('img');
    img.src = logoImg.getAttribute('src');
    if (logoImg.getAttribute('srcset')) img.srcset = logoImg.getAttribute('srcset');
    img.alt = '';
    img.width = logoImg.width || logoImg.naturalWidth;
    img.height = logoImg.height || logoImg.naturalHeight;
    link.append(img);
    bar.append(link);
  } else {
    bar.append(document.createElement('span'));
  }

  const end = document.createElement('div');
  end.className = 'mujo-drawer-bar__end';
  const searchIcon = document.querySelector('header search-button svg');
  if (searchIcon && document.getElementById('search-modal')) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-unstyled mujo-drawer-bar__search';
    button.setAttribute('aria-label', 'Open search');
    button.setAttribute('on:click', '#search-modal/showDialog');
    const wrapper = document.createElement('span');
    wrapper.className = 'svg-wrapper';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.append(searchIcon.cloneNode(true));
    button.append(wrapper);
    end.append(button);
  }
  bar.append(end);

  close.before(bar);
  start.append(close);
  syncDrawerBar(drawer);
};

const mountDrawer = () => {
  mountDrawerBar();
  document.querySelectorAll('template[data-curated-for]').forEach((template) => {
    const submenu = document.getElementById(template.dataset.curatedFor);
    const inner = submenu?.querySelector('.menu-drawer__inner-submenu');
    if (!inner || inner.querySelector('.mega-curated--drawer')) return;
    const list = inner.querySelector('.menu-drawer__menu--childlist');
    const content = template.content.cloneNode(true);
    if (list) {
      list.after(content);
    } else {
      inner.append(content);
    }
  });
};

/* ---------- Logo alignment ---------- */

/** Fraction of the logo image's width that is empty space before the lettering starts. */
const logoInkFraction = (img) => {
  const key = img.currentSrc || img.src;
  if (logoInkCache.has(key)) return logoInkCache.get(key);
  let fraction = 0;
  try {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (width && height) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, width, height).data;
      const base = [data[0], data[1], data[2], data[3]];
      search: for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          const i = (y * width + x) * 4;
          const diff =
            Math.abs(data[i] - base[0]) + Math.abs(data[i + 1] - base[1]) + Math.abs(data[i + 2] - base[2]) + Math.abs(data[i + 3] - base[3]);
          if (diff > 80) {
            fraction = x / width;
            break search;
          }
        }
      }
    }
  } catch {
    fraction = 0;
  }
  logoInkCache.set(key, fraction);
  return fraction;
};

const alignToLogo = (layouts) => {
  const root = document.documentElement;
  const logo = document.querySelector('header .header-logo__image');
  if (!logo || !logo.complete || !logo.getBoundingClientRect().width) return;
  const rect = logo.getBoundingClientRect();
  const inkLeft = rect.left + logoInkFraction(logo) * rect.width;

  const firstTitle = layouts[0]?.querySelector('.mega-menu__link-title');
  if (!firstTitle) return;
  const range = document.createRange();
  range.selectNodeContents(firstTitle);
  const textLeft = range.getBoundingClientRect().left - menuIndent;
  menuIndent = Math.max(0, Math.min(60, Math.round(inkLeft - textLeft)));
  root.style.setProperty('--mujo-menu-indent', `${menuIndent}px`);
};

/* ---------- Desktop sizing ---------- */

const desktopLayouts = () =>
  [...document.querySelectorAll('.mega-menu__layout--curated')].filter((layout) => !layout.closest('[slot="overflow"]'));

const measure = () => {
  if (window.innerWidth < 990) return;
  const layouts = desktopLayouts();
  if (!layouts.length) return;

  alignToLogo(layouts);

  const root = document.documentElement;
  const currentBleed = parseFloat(root.style.getPropertyValue('--mujo-feature-bleed')) || 0;
  let tallest = 0;
  let padding = 0;
  let bleed = 0;
  let inset = 0;
  let top = 0;

  layouts.forEach((layout) => {
    layout.querySelectorAll('.mega-menu__column').forEach((column) => {
      let height = 0;
      for (const child of column.children) {
        if (!child.classList.contains('mega-curated__preview')) height += child.getBoundingClientRect().height;
      }
      tallest = Math.max(tallest, height);
    });

    const inner = layout.closest('.menu-list__submenu-inner');
    if (inner) {
      const styles = getComputedStyle(inner);
      padding = Math.max(padding, parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom));
    }

    const feature = layout.querySelector(':scope > .mega-curated');
    if (feature) {
      const styles = getComputedStyle(layout);
      inset = parseFloat(styles.getPropertyValue('--feature-inset')) || 0;
      top = parseFloat(styles.getPropertyValue('--feature-top')) || inset;
      const gap = root.clientWidth - feature.getBoundingClientRect().right;
      bleed = Math.max(bleed, gap + currentBleed - inset);
    }
  });

  const ceiling = Math.min(MAX_FEATURE, window.innerWidth * 0.33);
  const size = Math.round(Math.min(ceiling, Math.max(MIN_FEATURE, tallest + padding)));
  bleedSize = Math.max(0, Math.round(bleed));
  squareSize = size - top - inset;
  root.style.setProperty('--mujo-feature-size', `${size}px`);
  root.style.setProperty('--mujo-feature-bleed', `${bleedSize}px`);
  measuredWidth = window.innerWidth;

  layouts.forEach((layout) => fitSquares(layout, true));
};

/** Decide how many squares fit beside this dropdown's links. */
const fitSquares = (layout, force = false) => {
  if (!squareSize || (!force && layout.dataset.squares)) return;
  const items = layout.querySelectorAll(':scope > .mega-curated .mega-curated__item').length;
  if (!items) return;

  const styles = getComputedStyle(layout);
  const inset = parseFloat(styles.getPropertyValue('--feature-inset')) || 0;
  const grid = layout.querySelector('.mega-menu__grid');
  const columns = parseInt(styles.getPropertyValue('--curated-link-columns'), 10) || 1;
  const linkGap = grid ? parseFloat(getComputedStyle(grid).columnGap) || 24 : 24;
  const layoutGap = parseFloat(styles.columnGap) || 48;
  const space = layout.getBoundingClientRect().width - menuIndent + (bleedSize - inset);
  const linksNeed = columns * MIN_LINK_COLUMN + (columns - 1) * linkGap + layoutGap;

  let fit = 1;
  for (let n = Math.min(items, 3); n >= 1; n--) {
    if (linksNeed + n * squareSize + (n - 1) * inset <= space) {
      fit = n;
      break;
    }
  }
  layout.dataset.squares = String(fit);
};

/* ---------- Desktop hover preview ---------- */

const loadImage = (item) => {
  const img = new Image();
  img.sizes = IMAGE_SIZES;
  img.srcset = item.srcset;
  img.src = item.src;
  return img.decode().catch(() => {});
};

const createController = (panel) => {
  const dataEl = panel.querySelector('[data-curated-previews]');
  const stage = panel.querySelector('.mega-curated__stage');
  const preview = panel.querySelector('.mega-curated__preview');
  const layout = panel.closest('.mega-menu__layout--curated');
  if (!dataEl || !stage || !preview || !layout) return null;

  let items = [];
  try {
    items = JSON.parse(dataEl.textContent);
  } catch {
    return null;
  }
  const lookup = new Map(items.map((item) => [normalise(item.href), item]));

  const warm = () => items.forEach(loadImage);
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(warm, { timeout: 800 });
  } else {
    setTimeout(warm, 200);
  }

  layout.querySelectorAll('.mega-menu__link[href]').forEach((link) => {
    if (normalise(link.getAttribute('href')) === currentPath) link.setAttribute('aria-current', 'page');
  });

  const image = preview.querySelector('img');
  const title = preview.querySelector('[data-preview-title]');
  const count = preview.querySelector('[data-preview-count]');
  image.sizes = IMAGE_SIZES;
  image.style.transition = 'opacity 0.2s ease';
  let hideTimer;
  let requestId = 0;

  const swapImage = (item) => {
    if (image.dataset.current === item.src) return;
    const id = ++requestId;
    image.dataset.current = item.src;
    image.style.opacity = '0';
    loadImage(item).then(() => {
      if (id !== requestId) return;
      image.srcset = item.srcset;
      image.src = item.src;
      image.hidden = false;
      image.style.opacity = '1';
    });
  };

  return {
    show(link) {
      const item = lookup.get(normalise(link.getAttribute('href')));
      if (!item) return;
      clearTimeout(hideTimer);
      swapImage(item);
      preview.href = item.href;
      title.textContent = item.title;
      if (count) count.textContent = item.count;
      preview.classList.add('is-visible');
      stage.classList.add('is-previewing');
    },
    keep() {
      clearTimeout(hideTimer);
    },
    hide() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        preview.classList.remove('is-visible');
        stage.classList.remove('is-previewing');
      }, 150);
    },
  };
};

const controllerFor = (element) => {
  const layout = element.closest('.mega-menu__layout--curated');
  const panel = layout?.querySelector('.mega-curated[data-preview]');
  if (!panel) return null;
  if (!controllers.has(panel)) controllers.set(panel, createController(panel));
  return controllers.get(panel);
};

const onEnter = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('header-menu')) {
    if (measuredWidth !== window.innerWidth) {
      measure();
    } else {
      // The header may have been re-rendered since we last measured
      desktopLayouts().forEach((layout) => fitSquares(layout));
    }
  }

  const previewEl = target.closest('.mega-curated__preview');
  if (previewEl) {
    controllerFor(previewEl)?.keep();
    return;
  }

  const layout = target.closest('.mega-menu__layout--curated');
  if (layout) controllerFor(layout);

  const link = target.closest(LINK_SELECTOR);
  if (link) controllerFor(link)?.show(link);
};

const onLeave = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const source = target.closest(LINK_SELECTOR) || target.closest('.mega-curated__preview');
  if (!source) return;
  if (event.relatedTarget instanceof Node && source.contains(event.relatedTarget)) return;
  controllerFor(source)?.hide();
};

if (!window.__mujoMenuPreview) {
  window.__mujoMenuPreview = true;
  document.addEventListener('pointerover', onEnter);
  document.addEventListener('focusin', onEnter);
  document.addEventListener('pointerout', onLeave);
  document.addEventListener('focusout', onLeave);

  // Mobile menu: build now, and again straight away whenever the theme re-renders the header
  mountDrawer();
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mountDrawer();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Backstop: build on a touch outside the open menu (e.g. the menu button) - never on a
  // touch inside it, so nothing moves under the customer's finger
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.target instanceof Element && event.target.closest('.menu-drawer')) return;
      mountDrawer();
    },
    { capture: true, passive: true }
  );

  requestAnimationFrame(measure);
  // Re-align once the logo image has loaded, if it wasn't ready yet
  const logo = document.querySelector('header .header-logo__image');
  if (logo && !logo.complete) logo.addEventListener('load', () => { measuredWidth = 0; mountDrawer(); }, { once: true });
}